import React from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PaperPlaneRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const fetcher = (u) => api.get(u).then((r) => r.data);

export const DEADLINE_TYPES = {
  tva: "TVA mensuelle",
  acompte_is: "Acompte IS",
  dsf: "DSF annuelle",
  patente: "Patente",
  cnps: "CNPS",
};

export const DEADLINE_STATUS = {
  en_retard: { label: "En retard", bg: "#FEF2F2", fg: "#991B1B" },
  urgent: { label: "Urgent (≤ 7 j)", bg: "#FFFBEB", fg: "#92400E" },
  a_venir: { label: "À venir", bg: "#F0EDF8", fg: "#2E3192" },
};

export const fmtXaf = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

/** Calendrier fiscal camerounais — échéances calculées depuis les dossiers comptables. */
export default function Echeances() {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = React.useState("tous");
  const [sending, setSending] = React.useState(false);
  const { data } = useSWR("/accounting/deadlines", fetcher);
  const items = (data?.items || []).filter((d) => statusFilter === "tous" || d.status === statusFilter);
  const counts = { en_retard: 0, urgent: 0, a_venir: 0 };
  (data?.items || []).forEach((d) => { counts[d.status] = (counts[d.status] || 0) + 1; });

  const sendDigest = async () => {
    setSending(true);
    try {
      const r = await api.post("/reminders/send-digest");
      toast.success(r.data.sent ? `Récapitulatif envoyé à ${r.data.sent} destinataire(s) — ${r.data.items} échéance(s)` : r.data.message);
    } catch (err) { toast.error(err?.response?.data?.detail || "Envoi impossible"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="echeances-page">
      <PageHeader
        eyebrow="Fiscalité"
        title="Échéances fiscales"
        description="Calendrier camerounais calculé depuis les dossiers comptables : TVA et acomptes IS avant le 15 du mois suivant, DSF annuelle, patente, CNPS."
      />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {Object.entries(DEADLINE_STATUS).map(([k, s]) => (
          <span key={k} className="text-xs px-3 py-1.5 rounded-full" style={{ background: s.bg, color: s.fg }} data-testid={`echeances-count-${k}`}>
            {s.label} : {counts[k]}
          </span>
        ))}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 ml-auto" data-testid="echeances-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tous">Tous les statuts</SelectItem>
            {Object.entries(DEADLINE_STATUS).map(([k, s]) => (<SelectItem key={k} value={k}>{s.label}</SelectItem>))}
          </SelectContent>
        </Select>
        {user?.role === "admin" && (
          <Button variant="outline" onClick={sendDigest} disabled={sending} data-testid="echeances-send-digest">
            <PaperPlaneRight size={14} className="mr-1" /> {sending ? "Envoi…" : "Envoyer le récapitulatif"}
          </Button>
        )}
      </div>

      <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F8FC] border-b border-[#E2E4F0]">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#1E2066]">
              <th className="px-4 py-2">Échéance</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Client</th>
              <th className="px-4 py-2">Période</th><th className="px-4 py-2 text-right">Montant estimé</th><th className="px-4 py-2">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500" data-testid="echeances-empty">Aucune échéance — créez des dossiers comptables et saisissez des écritures.</td></tr>}
            {items.map((d) => {
              const s = DEADLINE_STATUS[d.status] || DEADLINE_STATUS.a_venir;
              return (
                <tr key={d.id} className="hover:bg-[#F7F5FC]/60 transition-colors" data-testid={`echeance-row-${d.id}`}>
                  <td className="px-4 py-2 font-medium">{d.due_date}</td>
                  <td className="px-4 py-2">{DEADLINE_TYPES[d.type] || d.type}</td>
                  <td className="px-4 py-2">
                    <Link to={`/comptabilite/${d.file_id}`} className="text-[#2E3192] hover:underline">{d.client_name}</Link>
                  </td>
                  <td className="px-4 py-2 text-zinc-500">{d.period}</td>
                  <td className="px-4 py-2 text-right">{d.amount != null ? `${fmtXaf(d.amount)} ${d.currency}` : "—"}</td>
                  <td className="px-4 py-2"><span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.fg }}>{s.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-500 mt-3">{data?.note}</p>
    </div>
  );
}
